import { ColDef, ValueGetterParams } from 'ag-grid-community';
import { CustomCellRendererProps } from 'ag-grid-react';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Flex } from 'reflexbox';

import { CycleManagerMomentStatus, CycleMoment, CycleParticipant } from '@treams/treams-analytics';
import { useLocalize } from '@treams/treams-hooks';
import {
	Accordion,
	DataGrid,
	MiniProgressBar,
	Modal,
	Paragraph,
	Pill,
	Popover,
} from '@treams/treams-ui';

import { ProgressLabel } from './GridProgressCell';
import { MomentIcon } from './MomentIcon';

function participantName(participant: CycleParticipant) {
	return `${participant.firstName} ${participant.lastName}`.trim();
}

interface StatusRow {
	id: string;
	name: string;
	done: boolean;
}

function StatusPill({ done }: { done: boolean }) {
	const { t } = useTranslation();

	return (
		<Flex alignItems="center" height="100%">
			<Pill small tint={done ? 'green' : 'slateBlue'}>
				{done
					? t('analytics.cycles.managers.statuses.done')
					: t('analytics.cycles.managers.statuses.notDone')}
			</Pill>
		</Flex>
	);
}

function StatusTable({ rows, showStatus }: { rows: StatusRow[]; showStatus?: boolean }) {
	const { t } = useTranslation();

	const columnDefs = useMemo<ColDef<StatusRow>[]>(() => {
		const nameColumn: ColDef<StatusRow> = {
			headerName: t('analytics.cycles.managers.statuses.employee'),
			flex: 1,
			minWidth: 220,
			valueGetter: ({ data }: ValueGetterParams<StatusRow>) => data?.name ?? '',
			cellRenderer: ({ data }: CustomCellRendererProps<StatusRow>) => (data ? data.name : null),
		};

		if (!showStatus) {
			return [nameColumn];
		}

		return [
			nameColumn,
			{
				headerName: t('analytics.cycles.managers.statuses.status'),
				flex: 0,
				minWidth: 150,
				// Sort so people who still need to do this step surface first.
				valueGetter: ({ data }: ValueGetterParams<StatusRow>) => (data?.done ? 1 : 0),
				cellRenderer: ({ data }: CustomCellRendererProps<StatusRow>) =>
					data ? <StatusPill done={data.done} /> : null,
			},
		];
	}, [t, showStatus]);

	return (
		<DataGrid<StatusRow>
			rowData={rows}
			columnDefs={columnDefs}
			defaultColDef={{ sortable: true }}
		/>
	);
}

interface StepBucket {
	key: string;
	label: string;
	doneCount: number;
	total: number;
	rows: StatusRow[];
}

function sortByStatusThenName(a: StatusRow, b: StatusRow) {
	if (a.done !== b.done) {
		return a.done ? 1 : -1;
	}

	return a.name.localeCompare(b.name);
}

function toRow(participant: CycleParticipant, doneIDs: Set<string>): StatusRow {
	return {
		id: participant.id,
		name: participantName(participant) || participant.id,
		done: doneIDs.has(participant.id),
	};
}

function useStatusView(statuses: CycleManagerMomentStatus[], participants: CycleParticipant[]) {
	const { t } = useTranslation();

	return useMemo(() => {
		const total = participants.length;

		const notStarted = statuses.find((status) => status.key === 'notStarted');
		const steps = statuses.filter((status) => status.key !== 'notStarted');

		const stepBuckets = steps.map((status): StepBucket => {
			// A step lists exactly who reached it. Don't count later steps as well:
			// they can be reached without this one (the manager signs before the employee).
			const doneIDs = new Set(status.members.map((member) => member.id));

			return {
				key: status.key,
				label: t(`analytics.cycles.managers.table.${status.key}`),
				doneCount: doneIDs.size,
				total,
				rows: participants
					.map((participant) => toRow(participant, doneIDs))
					.sort(sortByStatusThenName),
			};
		});

		const notStartedMembers = (notStarted?.members ?? [])
			.map((participant) => toRow(participant, new Set<string>()))
			.sort((a, b) => a.name.localeCompare(b.name));

		return { total, stepBuckets, notStartedMembers };
	}, [statuses, participants, t]);
}

export function ManagerStatusModal({
	close,
	managerName,
	moment,
	participants,
	statuses,
}: {
	close: () => void;
	managerName: string;
	moment?: CycleMoment;
	participants: CycleParticipant[];
	statuses: CycleManagerMomentStatus[];
}) {
	const { t } = useTranslation();
	const { localize } = useLocalize();

	const { total, stepBuckets, notStartedMembers } = useStatusView(statuses, participants);

	return (
		<Popover center visible onDismiss={close}>
			<Modal>
				<Modal.Header
					title={
						<>
							<Flex alignItems="center">
								{moment && (
									<Box size={20} mr={1}>
										<MomentIcon momentType={moment.type} />
									</Box>
								)}
								{t('analytics.cycles.managers.statuses.title', {
									momentName: localize(moment?.title).text,
								})}
							</Flex>
							<Paragraph>
								{t('analytics.cycles.managers.statuses.manager', {
									manager: managerName,
								})}
							</Paragraph>
						</>
					}
					onClose={close}
				/>
				<Modal.Contents>
					{total === 0 ? (
						<Flex justifyContent="center" py={3}>
							<Paragraph color="slateBlue" style={{ fontStyle: 'italic' }}>
								{t('analytics.cycles.managers.statuses.none')}
							</Paragraph>
						</Flex>
					) : (
						<>
							{notStartedMembers.length > 0 && (
								<Accordion
									title={t('analytics.cycles.managers.table.notStarted')}
									headerRight={
										<Paragraph color="slateBlue" flexShrink={0} mr={2}>
											{notStartedMembers.length} / {total}
										</Paragraph>
									}
								>
									<StatusTable rows={notStartedMembers} />
								</Accordion>
							)}

							{stepBuckets.map((bucket) => (
								<Accordion
									key={bucket.key}
									title={bucket.label}
									headerRight={
										<Flex alignItems="center" flexShrink={0} mr={2} style={{ gap: 8 }}>
											<MiniProgressBar value={bucket.doneCount} total={bucket.total} />
											<ProgressLabel value={bucket.doneCount} total={bucket.total} />
										</Flex>
									}
								>
									<StatusTable rows={bucket.rows} showStatus />
								</Accordion>
							))}
						</>
					)}
				</Modal.Contents>
				<Modal.FormFooter
					saveTitle={t('analytics.cycles.managers.statuses.close')}
					onSave={close}
				/>
			</Modal>
		</Popover>
	);
}
